import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Calendar,
  User,
  ChevronDown,
  Users,
  Check,
  MessageSquare,
  Building2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import toast from "react-hot-toast";
import { ErrorHandlers } from "../../utils/errorHandler";
import reservationApi from "../../services/reservationApi";
import tableApi from "../../services/tableApi";
import type {
  BookingRequestDto,
  RestaurantTable,
} from "../../types/reservations";
import { useMergeGroups } from "../../context/MergeGroupContext";

interface AddReservationDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
}

export default function AddReservationDialog({
  open,
  onClose,
  onSuccess,
}: Readonly<AddReservationDialogProps>) {
  const { refreshMergeGroups } = useMergeGroups();
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [selectedFloorId, setSelectedFloorId] = useState<number>(1);

  // Merge group creation state (only shown when 2+ tables selected)
  const [mergeGroupName, setMergeGroupName] = useState("");
  const [mergeGroupColor, setMergeGroupColor] = useState("#3B82F6");

  const [formData, setFormData] = useState({
    customerName: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guestNo: 2,
    specialRequests: "",
    smsMessage: "",
  });

  const [isLoadingTables, setIsLoadingTables] = useState(false);

  const fetchTables = useCallback(async () => {
    // Only fetch tables when date AND time are selected
    if (!formData.date || !formData.time) {
      setTables([]);
      setSelectedTableIds([]);
      return;
    }
    
    setIsLoadingTables(true);
    try {
      // Fetch merge groups for the selected reservation date (not today!)
      await refreshMergeGroups(formData.date, selectedFloorId);
      
      // Fetch available tables
      const availableTables = await tableApi.getAvailableTablesByDateTime(
        formData.date, 
        formData.time, 
        selectedFloorId
      );
      
      setTables(availableTables);
      // Clear selected tables when floor changes
      setSelectedTableIds([]);
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast.error("Failed to fetch tables");
    } finally {
      setIsLoadingTables(false);
    }
  }, [selectedFloorId, formData.date, formData.time, refreshMergeGroups]);



  // Fetch all tables on component mount or floor change
  useEffect(() => {
    if (open) {
      fetchTables();
    }
  }, [open, fetchTables]);

  // Auto-generate SMS message when customer info or table selection changes
  useEffect(() => {
    if (!open || !formData.customerName || !formData.date || !formData.time) return;
    
    // Only auto-populate if message is empty or was previously auto-generated
    if (formData.smsMessage && !formData.smsMessage.includes('We look forward to welcoming you to Grand Pittu')) return;

    const tableInfo = selectedTableIds.length > 0
      ? ` You have been assigned to ${tables.filter(t => selectedTableIds.includes(t.id)).map(t => t.name).join(', ')}.`
      : '';

    const autoMessage = `Hi ${formData.customerName}, your reservation for ${formData.guestNo} people on ${formData.date} at ${formData.time} has been accepted!${tableInfo} We look forward to welcoming you to Grand Pittu.`;

    setFormData((prev) => ({
      ...prev,
      smsMessage: autoMessage
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableIds, tables, formData.customerName, formData.date, formData.time, formData.guestNo, open]);

  // Helper function: Render available table item
  const renderAvailableTable = (table: RestaurantTable) => {
    const handleToggle = () => handleTableToggle(table.id);
    return (
      <label
        key={table.id}
        className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
      >
        <Checkbox
          checked={selectedTableIds.includes(table.id)}
          onCheckedChange={handleToggle}
        />
        <span className="text-sm text-gray-900 flex-1">
          {table.name}
        </span>
        <span className="text-xs text-gray-500">
          {table.seats} seats · {table.status}
        </span>
      </label>
    );
  };

  const handleTableToggle = (tableId: number) => {
    setSelectedTableIds((prev) => {
      if (prev.includes(tableId)) {
        return prev.filter((id) => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const getSelectedTablesText = () => {
    if (selectedTableIds.length === 0) return "Select tables...";

    const selectedTableNames = tables
      .filter((t) => selectedTableIds.includes(t.id))
      .map((t) => t.name);

    return selectedTableNames.join(", ");
  };



  // Helper function: Validate form inputs
  const validateFormInputs = () => {
    if (
      !formData.customerName ||
      !formData.phone ||
      !formData.date ||
      !formData.time
    ) {
      toast.error("Please fill in all required fields");
      return false;
    }

    if (selectedTableIds.length === 0) {
      toast.error("Please select at least one table");
      return false;
    }

    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return false;
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (selectedTableIds.length >= 2 && mergeGroupName.trim() && mergeGroupName.trim().length < 2) {
      toast.error("Merge group name must be at least 2 characters");
      return false;
    }

    return true;
  };

  // Helper function: Reset form state
  const resetFormState = () => {
    setFormData({
      customerName: "",
      email: "",
      phone: "",
      date: "",
      time: "",
      guestNo: 2,
      specialRequests: "",
      smsMessage: "",
    });
    setSelectedTableIds([]);
    setMergeGroupName("");
    setMergeGroupColor("#3B82F6");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateFormInputs()) {
      return;
    }

    setLoading(true);

    try {
      const bookingRequest: BookingRequestDto = {
        username: formData.customerName,
        email: formData.email,
        phoneNo: formData.phone,
        date: formData.date,
        time: formData.time,
        guestNo: formData.guestNo,
        tableNo: selectedTableIds[0], // Use first table as primary for legacy support
        restaurantTableId: selectedTableIds,
        message: formData.specialRequests || undefined,
        // Send merge group data directly to backend (no localStorage)
        ...(selectedTableIds.length >= 2 && mergeGroupName.trim() && {
          mergeGroupName: mergeGroupName.trim(),
          mergeGroupColor: mergeGroupColor
        })
      };
      
      // Log merge group creation request
      // if (selectedTableIds.length >= 2 && mergeGroupName.trim()) {
      //   console.log('🔗 Sending merge group creation with booking:', {
      //     name: mergeGroupName.trim(),
        
        // Note: Merge group will be created on the backend as part of the booking process
        // No need to create it locally - we'll refresh from backend after success

      await reservationApi.createBooking(bookingRequest);

      toast.success("Reservation created successfully! 🎉");

      // Refresh merge groups from backend if merge group was created
      if (selectedTableIds.length >= 2 && mergeGroupName.trim()) {
        await refreshMergeGroups(formData.date, selectedFloorId);
      }

      resetFormState();
      onSuccess?.();
      onClose();
    } catch (error) {
      const isAbortError = error instanceof Error && (
        error.name === 'AbortError' ||
        error.message?.includes('AbortError') ||
        error.message?.includes('aborted') ||
        error.message?.includes('signal is aborted')
      );

      if (isAbortError) {
        console.warn("⚠️ Booking request was aborted, but it may have succeeded.");
        toast.success(
          "Reservation may have been created. Please check the reservations list.",
          { duration: 5000 }
        );
        resetFormState();
        onSuccess?.();
        onClose();
      } else {
        console.error("Error creating reservation:", error);
        ErrorHandlers.reservation(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    field: keyof typeof formData,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[55rem] max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Add New Reservation
          </DialogTitle>
          <DialogDescription>
            Create a new reservation for a guest by filling in the details below
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="space-y-4 overflow-y-auto flex-1 px-1 pb-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4" />
              Customer Information
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="customerName">Full Name <span className="text-red-600">*</span></Label>
                <Input
                  id="customerName"
                  type="text"
                  placeholder="Enter customer name"
                  value={formData.customerName}
                  onChange={(e) => handleChange("customerName", e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="customer@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number  <span className="text-red-600">*</span></Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="0771234567"
                  value={formData.phone}
                  onChange={(e) =>
                    handleChange(
                      "phone",
                      e.target.value.replaceAll(/\D/g, "").slice(0, 10)
                    )
                  }
                  maxLength={10}
                  required
                />
                <p className="text-xs text-gray-500">
                  10 digits only (e.g., 0771234567)
                </p>
              </div>
            </div>
          </div>

          {/* Reservation Details */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Reservation Details
            </h3>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="date">Date  <span className="text-red-600">*</span></Label>
                <Input
                  id="date"
                  type="date"
                  min={getTodayDate()}
                  value={formData.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Time  <span className="text-red-600">*</span></Label>
                {/* <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange("time", e.target.value)}
                  required
                /> */}

                <select
                  id="time"
                  value={formData.time}
                  onChange={(e) => handleChange("time", e.target.value)}
                  required
                  className="flex h-9 w-full !rounded-md border border-muted-foreground bg-gray-100 px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    >
                      {/* This option now acts as a placeholder */}
                      <option value="" disabled>Select Time Slot</option> 
                      
                      <option value="10:00">10:00 AM</option>
                      <option value="10:30">10:30 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="11:30">11:30 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="12:30">12:30 PM</option>
                      <option value="13:00">1:00 PM</option>
                      <option value="13:30">1:30 PM</option>    
                      <option value="14:00">2:00 PM</option>
                      <option value="14:30">2:30 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="15:30">3:30 PM</option>
                      <option value="16:00">4:00 PM</option>
                      <option value="16:30">4:30 PM</option>
                      <option value="17:00">5:00 PM</option>
                      <option value="17:30">5:30 PM</option>
                      <option value="18:00">6:00 PM</option>
                      <option value="18:30">6:30 PM</option>
                      <option value="19:00">7:00 PM</option>
                      <option value="19:30">7:30 PM</option>
                      <option value="20:00">8:00 PM</option>
                    </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="guestNo">Guest count</Label>
                <Input
                  id="guestNo"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.guestNo}
                  onChange={(e) =>
                    handleChange("guestNo", Number.parseInt(e.target.value) || 1)
                  }
                />
              </div>
            </div>

            {/* Floor Selection */}
            <div className="space-y-2">
              <Label htmlFor="floor">Select Floor</Label>
              <div className="flex items-center space-x-4">
                <Building2 className="w-4 h-4 text-gray-500" />
                <select
                  id="floor"
                  value={selectedFloorId}
                  onChange={(e) => setSelectedFloorId(Number(e.target.value))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>1st Floor</option>
                  <option value={2}>2nd Floor</option>
                  <option value={3}>Roof Top</option>
                </select>
              </div>
            </div>

            {/* Table Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="tables">Select Tables  <span className="text-red-600">*</span></Label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTableDropdown(!showTableDropdown)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                  >
                    <span
                      className={
                        selectedTableIds.length === 0
                          ? "text-gray-400"
                          : "text-gray-900"
                      }
                    >
                      {getSelectedTablesText()}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>

                  {showTableDropdown && (() => {
                    const noDateOrTime = !formData.date || !formData.time;
                    const hasNoTables = tables.length === 0;
                    
                    if (noDateOrTime) {
                      return (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          <div className="px-3 py-4 text-sm text-amber-600 flex items-center justify-center gap-2 bg-amber-50">
                            <Calendar className="h-4 w-4" />
                            Please select date and time first
                          </div>
                        </div>
                      );
                    }
                    
                    if (isLoadingTables) {
                      return (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          <div className="px-3 py-4 text-sm text-gray-500 flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading available tables...
                          </div>
                        </div>
                      );
                    }
                    
                    if (hasNoTables) {
                      return (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No tables available
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">(
                        <div className="p-2">
                          {/* Group tables by floor */}
                          {Array.from(new Set(tables.map(t => t.floor))).sort((a, b) => {
                            const numA = a ? Number.parseInt(a) : 0;
                            const numB = b ? Number.parseInt(b) : 0;
                            return numA - numB;
                          }).map((floor, floorIndex) => {
                            const floorTables = tables.filter(t => t.floor === floor);

                            return (
                              <div key={`floor-${floor}-${floorIndex}`}>
                                {/* Floor Header */}
                                <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded mt-1 mb-1">
                                  Floor {floor}
                                </div>

                                {/* All Available Tables */}
                                {floorTables.map(renderAvailableTable)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <p className="text-xs text-gray-500">
                  {selectedTableIds.length} table(s) selected
                </p>
              </div>

              {/* Inline Merge Group Creation - Shows when 2+ tables selected */}
              {selectedTableIds.length >= 2 && (
                <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <h4 className="font-medium text-blue-900">
                      Create Table Group (Optional)
                    </h4>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="mergeGroupName" className="text-sm">
                        Group Name
                      </Label>
                      <Input
                        id="mergeGroupName"
                        value={mergeGroupName}
                        onChange={(e) => setMergeGroupName(e.target.value)}
                        placeholder="e.g., Birthday Party Section, VIP Lounge"
                        className="bg-white"
                      />
                      <p className="text-xs text-blue-600">
                        Merge these tables into a permanent group for easier
                        management
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Group Color</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { name: "Blue", value: "#3B82F6" },
                          { name: "Green", value: "#10B981" },
                          { name: "Purple", value: "#8B5CF6" },
                          { name: "Orange", value: "#F59E0B" },
                          { name: "Red", value: "#EF4444" },
                          { name: "Pink", value: "#EC4899" },
                          { name: "Indigo", value: "#6366F1" },
                          { name: "Teal", value: "#14B8A6" },
                        ].map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setMergeGroupColor(color.value)}
                            className={`h-10 rounded-md border-2 transition-all hover:scale-105 ${
                              mergeGroupColor === color.value
                                ? "border-blue-600 ring-2 ring-blue-300"
                                : "border-gray-300"
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          >
                            {mergeGroupColor === color.value && (
                              <Check className="h-5 w-5 text-white mx-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded-md border border-blue-200">
                      <span className="text-sm text-gray-700">
                        Total Seats:
                      </span>
                      <span className="text-lg font-bold text-blue-600">
                        {tables
                          .filter((t) => selectedTableIds.includes(t.id))
                          .reduce((sum, t) => sum + (t.seats || 0), 0)}{" "}
                        seats
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Special Requests */}
            <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="specialRequests">Special Requests</Label>
              <textarea
                id="specialRequests"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Any special dietary requirements, seating preferences, etc."
                value={formData.specialRequests}
                onChange={(e) =>
                  handleChange("specialRequests", e.target.value)
                }
              />
            </div>

            {/* SMS Message */}
            <div className="space-y-2">
              <Label htmlFor="smsMessage" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                SMS Message
              </Label>
              <textarea
                id="smsMessage"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Send a confirmation message to the customer..."
                value={formData.smsMessage}
                onChange={(e) => handleChange("smsMessage", e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Message to: {formData.phone || 'N/A'}
              </p>
            </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3 pt-4 border-t flex-shrink-0 bg-white">
            <Button
              type="button"
              variant="red"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              variant="blueBg"
            >
              {loading ? "Creating..." : "Create Reservation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
